/* eslint-disable @next/next/no-img-element */
/**
 * BookingLocationModal: the location picker for the straight-to-service booking
 * variant. Lets a user pick a market by selecting a saved address (rendered as
 * cards), entering a new address (autocomplete), or tapping a market button.
 * Composed into a responsive Modal (full-screen on mobile, centered dialog on
 * desktop). Address→market resolution is reused from AddressAutocomplete; this
 * component supplies the chrome, the saved-address cards, and commit wiring.
 */
import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import Modal from './Modal'
import AddressAutocomplete from './AddressAutocomplete'
import { Header3, Text, Button } from '../../../../styles/Components'
import { mq } from '../../../../helpers'
import { gsColors } from '../../../../styles/Variables'
import { useMarketContext } from '../../../MarketContext'

const emptyAddress = {
  id: '', street: '', city: '', state: '', zip: '',
  apartment: '', notes: '', type: '', pets: '', latitude: 0, longitude: 0,
  formatted: '', name: '', marketId: '',
}

const LocationBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 40px 40px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;

  > * {
    flex-shrink: 0;
  }

  ${mq('max', '768px')} {
    padding: 0 16px 30px;
  }

  .modal-address-input {
    width: 100%;
    max-width: 624px;
    margin: 0 auto;
    padding: 0;
  }

  .address-cards + .modal-address-input {
    margin-top: 20px;
  }

  /* The availability feedback sits directly above Continue, so give it a gap.
     Scoped to the success/error states because the message element is always
     rendered — an unconditional margin would pad the input when it is empty. */
  .modal-address-input .autocomplete-message.success,
  .modal-address-input .autocomplete-message.error {
    margin-bottom: 16px;
  }

  .modal-continue {
    align-self: flex-start;
    min-width: auto;
    font-size: 15px;
    line-height: 22px;

    ${mq('min', '768px')} {
      align-self: center;
    }

    &:disabled,
    &:disabled:hover {
      background: #c8c8cc;
      color: ${gsColors.mediumGrey};
      cursor: not-allowed;

      &::after {
        display: none;
      }
    }
  }

  .divider {
    width: 100%;
    max-width: 480px;
    height: 1px;
    background: ${gsColors.sand};
    margin: 28px 0;

    ${mq('max', '768px')} {
      width: calc(100% + 32px);
      max-width: none;
      margin-left: -16px;
      margin-right: -16px;
    }
  }

  .browse-label {
    color: inherit;
    font-weight: 600;
    margin: 0 0 16px;
  }

  .address-cards {
    display: flex;
    flex-wrap: nowrap;
    gap: 16px;
    width: fit-content;
    max-width: 100%;
    margin: 0 auto;
    overflow-x: auto;
    padding-bottom: 6px;
  }

  .address-card {
    cursor: pointer;
    box-sizing: border-box;
    background: #fff;
    border: 1px solid ${gsColors.textGrey};
    flex: 0 0 220px;
    min-height: 125px;
    padding: 18px 20px;
    text-align: left;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .card-label {
      color: ${gsColors.tangerine};
      font-family: Euclid;
      font-size: 12px;
      line-height: 18px;
    }

    .card-address {
      color: ${gsColors.black};
      font-family: Euclid;
      font-size: 16px;
      line-height: 1.38;
    }

    &.active {
      border-color: ${gsColors.cobalt};
    }
  }

  .add-new-address {
    all: unset;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: ${gsColors.cobalt};
    font-family: Euclid;
    font-size: 16px;
    font-weight: 600;
    margin: 20px 0 0;

    img {
      width: 22px;
      height: 22px;
    }
  }

  .markets {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;

    ${mq('max', '768px')} {
      width: 100%;
      justify-content: flex-start;
    }
  }

  .market-button {
    cursor: pointer;
    box-sizing: border-box;
    background: #fff;
    border: 1px solid ${gsColors.sand};
    padding: 10px 16px;
    font-family: Euclid;
    font-size: 18px;
    line-height: 1.38;
    color: ${gsColors.black};
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &:hover:not(.active) {
      border-color: ${gsColors.cobalt};
      color: ${gsColors.cobalt};
    }

    &.active {
      background: ${gsColors.black};
      border-color: ${gsColors.black};
      color: ${gsColors.white};
    }
  }
`

/**
 * BookingLocationModal: renders a modal that lets a user pick a market via a
 * saved-address card, address autocomplete, or a market button. Saved-address
 * and market-button taps commit and close immediately. When the user has saved
 * addresses, the cards stay visible and "Add new address" reveals the search +
 * Continue beneath them (rather than replacing the cards), so the user can
 * always fall back to a saved address. The address search is a plain select: it
 * commits the resolved market/address and enables the Continue button, which
 * closes the modal. `method` is one of 'address' | 'market-button' |
 * 'saved-address'.
 */
const BookingLocationModal = ({
  isOpen,
  close,
  currentMarket,
  address,
  addresses,
  markets = [],
  isDesktop,
  serviceRequests = [],
  allMarketServices = [],
  onCommit,
}) => {
  const { setSelectedMarket } = useMarketContext()
  const savedAddresses = addresses || []
  const hasSavedAddress = !!savedAddresses.length
  const [addingNew, setAddingNew] = useState(false)
  const canContinue = Boolean(address?.formatted || address?.street)

  useEffect(() => {
    if (!isOpen) {
      setAddingNew(false)
    }
  }, [isOpen])

  const commit = ({ market, address: committedAddress, method }) => {
    if (!market) {
      return
    }
    setSelectedMarket(market)
    onCommit({ market, address: committedAddress, method })
    close()
  }

  const autocompleteDispatch = ({ market, address: nextAddress }) => {
    if (!market) {
      return
    }
    setSelectedMarket(market)
    onCommit({ market, address: nextAddress, method: 'address' })
  }

  const selectMarketButton = m => () =>
    commit({ market: m, address: emptyAddress, method: 'market-button' })

  const selectSavedAddress = savedAddress => () =>
    commit({
      market: markets.find(m => m.id === savedAddress.marketId),
      address: savedAddress,
      method: 'saved-address',
    })

  return (
    <Modal
      isOpen={isOpen}
      closeModal={close}
      width="720px"
      isDesktop={isDesktop}
      closeTimeoutMS={isDesktop ? undefined : 300}
      portalClassName={isDesktop ? undefined : 'location-sheet-portal'}
      customStyles={{
        borderRadius: isDesktop ? '0' : '20px 20px 0 0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: isDesktop ? 'calc(100vh - 145px)' : 'calc(100vh - 60px)',
        top: isDesktop ? '105px' : 'auto',
        bottom: isDesktop ? 'auto' : '0',
        ...(isDesktop ? {} : { left: '0', right: '0' }),
      }}
    >
      <LocationBody>
        <Header3 fontSize={28} lineHeight="140%" align="center" margin="0 0 16px">
          Where would you like your glam done?
        </Header3>
        <Text lineHeight="140%" margin="0 0 24px">
          Service and beauty pro availability may vary by location.
        </Text>

        {hasSavedAddress && (
          <>
            <div className="address-cards">
              {savedAddresses.map(savedAddress => (
                <button
                  key={savedAddress.id}
                  type="button"
                  className={`address-card${address?.id === savedAddress.id ? ' active' : ''}`}
                  onClick={selectSavedAddress(savedAddress)}
                >
                  <span className="card-label">{savedAddress.name || savedAddress.type}</span>
                  <span className="card-address">
                    {[savedAddress.street, savedAddress.apartment, savedAddress.city, savedAddress.state, savedAddress.zip]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </button>
              ))}
            </div>
            {!addingNew && (
              <button type="button" className="add-new-address" onClick={() => setAddingNew(true)}>
                <img src="/images/add-address-circle-cobalt.svg" alt="" aria-hidden="true" />
                Add new address
              </button>
            )}
          </>
        )}

        {(!hasSavedAddress || addingNew) && (
          <>
            <AddressAutocomplete
              className="modal-address-input"
              currentMarket={currentMarket}
              hasSelectedServices={!!serviceRequests.length}
              serviceRequests={serviceRequests}
              allMarketServices={allMarketServices}
              dispatch={autocompleteDispatch}
            />
            <Button
              className="modal-continue"
              type="button"
              padding="13px 24px"
              onClick={close}
              disabled={!canContinue}
            >
              Continue
            </Button>
          </>
        )}

        <div className="divider" />
        <Text className="browse-label" fontSize={14}>or browse by location</Text>
        <div className="markets">
          {markets.filter(m => m.isEnabled).map(m => (
            <button
              key={m.id}
              type="button"
              className={`market-button${currentMarket?.id === m.id ? ' active' : ''}`}
              onClick={selectMarketButton(m)}
            >
              {m.name}
            </button>
          ))}
        </div>
      </LocationBody>
    </Modal>
  )
}

export default BookingLocationModal
